import { useState, useEffect, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import SmallLoading from '@/components/SmallLoading'
import AccessManagement from './AccessManagement'
import EmailLists from './EmailLists'

import * as S from './styles'

import type {
  RelatedUser,
  RelatedUsersList
} from '@/types/components/Modal/Templates/ShareCollection'

interface EmailFormProps {
  onShare: (users: RelatedUsersList) => Promise<boolean>
  allUsers: RelatedUsersList
  onCancel: () => void
  accessList: RelatedUsersList
  onShowManagementView: () => void
  isShowingTabSelector: boolean
  changeTabSelectorVisibility: () => void
}

export default function EmailForm(props: EmailFormProps) {
  const {
    onShare,
    allUsers,
    onCancel,
    accessList,
    onShowManagementView,
    isShowingTabSelector,
    changeTabSelectorVisibility
  } = props

  const { t } = useTranslation()

  const [labels, setLabels] = useState({
    shareText: '',
    labelText: '',
    placeholderText: '',
    cancelText: '',
    successSubtitleText: '',
    errorSubtitleText: '',
    errorInputText: '',
    loadingAlt: ''
  })
  const [currentInputValue, setCurrentInputValue] = useState('')
  const [errorInput, setErrorInput] = useState(false)
  const [suggestionsList, setSuggestionsList] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [listedUsers, setListedUsers] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const insertedValue = e.target.value
    setCurrentInputValue(insertedValue)

    if (
      // @ts-expect-error review this
      e.nativeEvent.inputType === 'deleteContentBackward' &&
      insertedValue === ''
    ) {
      setErrorInput(false)
      return
    }

    if (insertedValue.length < 3) {
      setSuggestionsList([])
      return
    }

    if (
      insertedValue.length < 3 &&
      // @ts-expect-error review this
      e.nativeEvent.inputType === 'deleteContentBackward'
    ) {
      setSuggestionsList([])
      return
    }

    if (insertedValue) {
      const newSuggestionList = listedUsers.filter(item => {
        const lowerCaseInsertedValue = insertedValue.toLowerCase()
        return item.email.includes(lowerCaseInsertedValue)
      })

      setSuggestionsList(newSuggestionList)
    }
  }

  const handleShare = async (event: FormEvent<HTMLFormElement>) => {
    setIsLoading(true)

    event.preventDefault()

    const result = await onShare(selectedUsers)

    if (result) {
      setSelectedUsers([])
    }

    setIsLoading(false)
  }

  const handleSuggestionClick = (user: RelatedUser) => {
    setSelectedUsers(state => state.concat(user))
    setListedUsers(state =>
      state.filter(item => item.cognito_id !== user.cognito_id)
    )

    setSuggestionsList([])
    setCurrentInputValue('')
  }

  const handleRemoveSelectedUser = (removedUser: RelatedUser) => {
    setSelectedUsers(state =>
      state.filter(user => user.cognito_id !== removedUser.cognito_id)
    )
    setListedUsers(state => state.concat(removedUser))
  }

  useEffect(() => {
    setLabels({
      shareText: t('buttons.share'),
      labelText: t('labels.shareCollection'),
      placeholderText: t('placeholders.shareCollection'),
      cancelText: t('buttons.cancel'),
      successSubtitleText: t('subtitles.subtitleSuccessShareCollection'),
      errorSubtitleText: t('subtitles.subtitleErrorShareCollection'),
      errorInputText: t('messages.errorInputShared'),
      loadingAlt: t('alts.load')
    })
  }, [t])

  useEffect(() => {
    if (
      currentInputValue.length > 6 &&
      currentInputValue.includes('@') &&
      !listedUsers.length
    ) {
      setErrorInput(true)
    } else {
      setErrorInput(false)
    }
  }, [currentInputValue, listedUsers])

  useEffect(() => {
    if (!allUsers) {
      return
    }

    const accessListEmails = new Set(accessList.map(user => user.email))
    const usersToList = allUsers.filter(
      user => !accessListEmails.has(user.email)
    )

    setListedUsers(usersToList)
  }, [allUsers, accessList])

  return (
    <form
      onSubmit={handleShare}
      style={{
        marginTop: isShowingTabSelector ? '0' : '33px'
      }}
    >
      <label htmlFor={labels.shareText}>{labels.labelText}</label>
      <input
        type="text"
        placeholder={labels.placeholderText}
        name={labels.shareText}
        onChange={handleInputChange}
        autoComplete="off"
        style={{
          border: `${errorInput ? '1px solid #F03D3E' : '1px solid #e1e1e1'}`
        }}
        value={currentInputValue}
        id={'inp_share'}
        disabled={isLoading}
        onFocus={isShowingTabSelector ? changeTabSelectorVisibility : null}
      />
      {errorInput && <p className="inputError">{labels.errorInputText}</p>}
      {!!suggestionsList.length && (
        <S.SharedListSuggestions absolute={!!selectedUsers.length}>
          {suggestionsList.map(item => (
            <li
              key={item.id}
              onClick={handleSuggestionClick.bind(null, item)}
              id={`add_email_list`}
            >
              {item.email}
            </li>
          ))}
        </S.SharedListSuggestions>
      )}
      {!!selectedUsers.length && (
        <EmailLists
          lists={[{ key: 'selected-users', users: selectedUsers }]}
          onRemove={handleRemoveSelectedUser}
          disabled={isLoading}
        />
      )}
      {!!accessList.length && isShowingTabSelector && (
        <AccessManagement
          usersList={accessList}
          onShowManagementView={onShowManagementView}
        />
      )}
      <S.SharedListButtons>
        <button
          type="button"
          onClick={
            isShowingTabSelector ? onCancel : changeTabSelectorVisibility
          }
          id={isShowingTabSelector ? 'btn_cancel_share' : 'btn_return_share'}
          disabled={isLoading}
        >
          {isShowingTabSelector ? t('buttons.cancel') : t('buttons.return')}
        </button>
        <button
          type="submit"
          disabled={!selectedUsers.length || errorInput || isLoading}
          id="btn_share"
          className={isLoading ? 'loading' : ''}
        >
          {labels.shareText}

          <SmallLoading className="loading-indicator" />
        </button>
      </S.SharedListButtons>
    </form>
  )
}
